import { useSyncExternalStore } from 'react';

/**
 * A shared clock for relative timestamps.
 *
 * Reading `Date.now()` during render is impure — React may re-render at any
 * moment and the result would drift unpredictably. This exposes the current
 * time as an external store instead, which is the sanctioned way to read a
 * mutable outside source.
 *
 * One interval serves every subscriber, so a thread with fifty timestamps ticks
 * once a minute rather than fifty times.
 */
const TICK_MS = 60_000;

let now = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    timer = setInterval(() => {
      now = Date.now();
      listeners.forEach((l) => l());
    }, TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

const getSnapshot = () => now;

export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Parse an API timestamp, treating a missing timezone as UTC.
 *
 * Rows read back from Mongo come through without a timezone designator
 * (pymongo returns naive datetimes, so pydantic serializes "…T22:38:15.544000"
 * with no trailing Z). ECMAScript parses a date-time with no designator as
 * LOCAL time, which put every stored message hours into the future — and a
 * future timestamp fell into the "< 60 seconds" branch, so everything read
 * "just now" until the wall clock caught up.
 *
 * The backend now stamps UTC on the way out, but parsing defensively here also
 * covers timestamps from endpoints that don't (artifact created_at, for one).
 */
export function parseApiDate(iso: string): number {
  if (!iso) return NaN;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso.trim());
  return Date.parse(hasZone ? iso : `${iso}Z`);
}

export function relativeLabel(iso: string, nowMs: number): string | null {
  const then = parseApiDate(iso);
  if (Number.isNaN(then)) return null;

  const secs = Math.round((nowMs - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(then).toLocaleDateString();
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
